/**
 * The whole setup in one file (contract v65).
 *
 * The viewer writes and reads the scene itself — lights, environments,
 * metals, stones, camera, effects, every packed file — as an of-scene/2
 * document (`exportSceneFile` / `exportSceneJSON` / `importScene`). The
 * studio adds what only it knows, in the document's `host` block
 * (`sceneHost.ts`): the customer configurator, the watermark and the On Body
 * panel.
 *
 * Every flow here is gated on caps `sceneFileV65` by its callers: the four
 * methods are new names and RPC has no timeout.
 *
 * - Save scene file: everything except the ring, every file packed inside.
 * - Open scene file: REPLACES the look exactly, as ONE Undo step. Undo
 *   re-opens a snapshot taken just before (`assets: "session"` — handles into
 *   the viewer's own asset store, nothing re-downloaded); Redo re-opens one
 *   taken just after.
 * - Ring + scene: the ring as a 3D file with the whole scene inside, so
 *   opening that one file brings back the ring AND its look.
 */
import type { ViewerClient } from "../../../client/ViewerClient";
import type { GroupInfo, SceneAppliedEventPayload, SceneImportResult } from "../../../shared/api-contract";
import type { EditorStore } from "../../../state/editor/EditorStore";
import type { UiState } from "../../../state/editor/ui";
import { downloadBlob } from "../../../util/download";
import { isGlbHead, sniffSceneFile } from "../../../util/sceneFileSniff";
import { hasChoices, type ConfiguratorDef } from "../../../features/vault/configurator";
import { basicEngineFor } from "../basic/applyEngine";
import { configuratorDraftFor } from "../configurator/draft";
import { leaveCustomerPreview } from "../configurator/previewExit";
import { onBodySessionFor, onBodySessionIfAny } from "../onbody/session";
import {
  configuratorTargetsOf,
  parseStudioHost,
  remapConfigurator,
  STUDIO_HOST_APP,
  STUDIO_HOST_VERSION,
  type StudioSceneHost,
} from "./sceneHost";

/** The plan's cap on a file the studio will hand the viewer to open. */
export const MAX_SCENE_FILE_BYTES = 200 * 1024 * 1024;

// ── which editor a viewer belongs to ────────────────────────────────────────

/**
 * The editor store per viewer, for the one caller outside the editor's
 * providers: the Vault's top-bar Save (`StudioPage`) renders beside the
 * editor, not inside it, and still has to save the watermark and On Body with
 * the scene. Registered by `SceneFileBridge` while the editor is mounted.
 */
const stores = new WeakMap<ViewerClient, EditorStore>();

export function registerSceneHost(client: ViewerClient, store: EditorStore): () => void {
  stores.set(client, store);
  return () => {
    if (stores.get(client) === store) stores.delete(client);
  };
}

// ── the host block ──────────────────────────────────────────────────────────

const draftReady = (s: { load: string; modelId: string | undefined }) => s.load === "ready" || (s.load === "idle" && !s.modelId);

/**
 * The studio's half of the scene, right now. `configurator: false` leaves the
 * configurator out (the Vault saves its own, separately); otherwise it is the
 * draft being edited — or nothing at all while the draft is still loading,
 * so a half-loaded def can never be saved as "no configurator".
 */
export function captureHost(client: ViewerClient, store: EditorStore | null, opts: { configurator?: boolean } = {}): StudioSceneHost {
  const host: StudioSceneHost = { app: STUDIO_HOST_APP, version: STUDIO_HOST_VERSION };
  if (store) {
    const ui = store.getState("ui") as UiState | undefined;
    if (ui?.watermark) host.watermark = { ...ui.watermark };
    if (opts.configurator !== false) {
      const draft = configuratorDraftFor(store).getState();
      if (draftReady(draft)) {
        const def = draft.def;
        const any = def.groups.length > 0 || def.basePrice !== undefined || !!def.variants?.length;
        host.configurator = any ? def : null;
        if (any) host.configuratorTargets = configuratorTargetsOf(def, (store.getState("groups") as GroupInfo[] | undefined) ?? []);
      }
    }
  }
  host.onBody = onBodySessionIfAny(client)?.hostSnapshot() ?? null;
  return host;
}

/** `captureHost` for a caller outside the editor; null when no editor is mounted on this viewer. */
export function captureHostFor(client: ViewerClient, opts: { configurator?: boolean } = {}): StudioSceneHost | null {
  const store = stores.get(client);
  return store ? captureHost(client, store, opts) : null;
}

/**
 * Whether a host block may replace the configurator being edited:
 * `replace` — the user opened this scene; `skip` — it arrived with a load, and
 * a Vault model's own saved configurator outranks a copy inside a file.
 */
export type ConfiguratorPolicy = "replace" | "skip";

const sameJson = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);

/**
 * Put the studio's half of a scene back. `raw` is untrusted (it came out of a
 * file) and is validated field by field; a block that is not the studio's
 * changes nothing. `groupMap` is the viewer's saved-part → live-part map from
 * the same import, which re-points each configurator slot at its part.
 */
export async function applyHost(
  client: ViewerClient,
  store: EditorStore,
  raw: unknown,
  groupMap: Record<string, string[]> | null | undefined,
  policy: ConfiguratorPolicy,
): Promise<boolean> {
  const host = parseStudioHost(raw);
  if (!host) return false;

  if (host.watermark) {
    const ui = store.getState("ui") as UiState;
    if (!sameJson(ui.watermark, host.watermark)) store.setState("ui", { ...ui, watermark: host.watermark });
  }

  if (policy === "replace" && host.configurator !== undefined) {
    const draft = configuratorDraftFor(store);
    if (draftReady(draft.getState())) {
      const saved: ConfiguratorDef = host.configurator ?? { groups: [] };
      const live = (await client.ring.listGroups().catch(() => null)) ?? (store.getState("groups") as GroupInfo[] | undefined) ?? [];
      const def = remapConfigurator(saved, host.configuratorTargets, groupMap, live);
      draft.edit("groups", (d) => (sameJson(d.groups, def.groups) ? d : { ...d, groups: def.groups }));
      draft.edit("pricing", (d) =>
        d.basePrice === def.basePrice && d.currency === def.currency ? d : { ...d, basePrice: def.basePrice, currency: def.currency },
      );
      draft.edit("variants", (d) => (sameJson(d.variants, def.variants) ? d : { ...d, variants: def.variants }));
      draft.setActiveSlot(null);
      if (hasChoices(def)) draft.setOn(true);
    }
  }

  if (host.onBody) await onBodySessionFor(client).adoptHost(host.onBody);
  else if (host.onBody === null) await onBodySessionIfAny(client)?.adoptHost(null);
  return true;
}

/**
 * A scene replays the camera, effects, every material and the backdrop, so
 * every slice the panels render from is stale the moment it lands.
 */
export async function resyncAfterScene(store: EditorStore): Promise<void> {
  // Selection first: the material slice reads which part is selected.
  await store.refresh("selection");
  await Promise.all(
    (["scene", "postFX", "renderer", "groups", "material", "animation"] as const).map((d) => store.refresh(d)),
  );
}

// ── the studio's own imports vs everyone else's ─────────────────────────────

/**
 * Every `importScene` fires `sceneApplied`. The studio's own opens (and their
 * Undo / Redo) apply the host block themselves, inside the step, so the
 * listener must not apply it a second time. One token per own import, in
 * order; a token is dropped when its import ends, whether or not its event
 * came, so a viewer that never emits cannot leave a stale token behind to
 * swallow somebody else's event.
 *
 * Why the event cannot arrive after its token is dropped: an own import ends
 * only after `resyncAfterScene`, which is several more round trips, and the
 * viewer's messages reach the host in the order it posts them.
 */
const ownImports = new WeakMap<ViewerClient, object[]>();

function beginOwnImport(client: ViewerClient): () => void {
  let q = ownImports.get(client);
  if (!q) {
    q = [];
    ownImports.set(client, q);
  }
  const token = {};
  q.push(token);
  return () => {
    const i = q.indexOf(token);
    if (i >= 0) q.splice(i, 1);
  };
}

/** Is this `sceneApplied` from one of the studio's own imports? Consumes it if so. */
export function claimOwnSceneApplied(client: ViewerClient): boolean {
  return !!ownImports.get(client)?.shift();
}

/** Import a scene (replace), then the studio's half, then re-read every panel. */
async function importOwn(
  client: ViewerClient,
  store: EditorStore,
  source: File | Blob | string,
  policy: ConfiguratorPolicy = "replace",
): Promise<SceneImportResult> {
  const done = beginOwnImport(client);
  try {
    const result = await client.ring.importScene(source, { mode: "replace" });
    await applyHost(client, store, result.host, result.groupMap, policy);
    await resyncAfterScene(store);
    return result;
  } finally {
    done();
  }
}

/**
 * A `sceneApplied` the studio did not start: a model whose file carried its
 * scene (`origin: "model"`), or a scene opened through another route (a Vault
 * save, a settings file the viewer recognised as a whole scene). Returns what
 * it did, so the caller can say so.
 */
export async function onSceneApplied(
  client: ViewerClient,
  store: EditorStore,
  p: SceneAppliedEventPayload,
): Promise<"own" | "applied"> {
  if (p.origin === "file" && claimOwnSceneApplied(client)) return "own";
  // A Vault model's own configurator (config.configurator) outranks the copy in its file.
  const policy: ConfiguratorPolicy = configuratorDraftFor(store).getState().modelId ? "skip" : "replace";
  await applyHost(client, store, p.result?.host ?? null, p.result?.groupMap, policy);
  await resyncAfterScene(store);
  return "applied";
}

// ── checking a file before the viewer sees it ───────────────────────────────

/** Refuse, in plain words, a file that cannot be a scene — before any of the scene is touched. */
export async function checkSceneFile(file: Blob): Promise<void> {
  if (file.size > MAX_SCENE_FILE_BYTES) throw new Error("That file is too large to open (over 200 MB).");
  if (await sniffSceneFile(file)) return;
  const head = new Uint8Array(await file.slice(0, 4).arrayBuffer());
  if (isGlbHead(head)) {
    throw new Error("That 3D file has no saved setup inside. To open the ring itself, drop it on the canvas or use Replace model.");
  }
  // An older settings file: the viewer reads those too, and upgrades them on the way in.
  try {
    const doc = JSON.parse(await file.text()) as unknown;
    if (doc && typeof doc === "object" && !Array.isArray(doc)) return;
  } catch {
    /* falls through */
  }
  throw new Error("That file has no saved scene in it.");
}

// ── the three actions ───────────────────────────────────────────────────────

export interface SavedFile {
  filename: string;
  bytes: number;
}

/** A file name from the piece's name: readable, safe on every OS. */
export function fileBase(name: string | null | undefined): string {
  const base = (name ?? "")
    .replace(/\.[a-z0-9]{1,5}$/i, "")
    // eslint-disable-next-line no-control-regex
    .replace(/[\\/:*?"<>|\u0000-\u001f]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, 80);
  return base || "ring";
}

/** "860 KB", "12.4 MB". */
export function formatBytes(n: number): string {
  if (n < 1024 * 1024) return `${Math.max(1, Math.round(n / 1024))} KB`;
  return `${(n / (1024 * 1024)).toFixed(n < 10 * 1024 * 1024 ? 1 : 0)} MB`;
}

/** Save scene file: everything except the ring, every file packed inside. */
export async function saveSceneFile(client: ViewerClient, store: EditorStore, modelName?: string | null): Promise<SavedFile> {
  const blob = await client.ring.exportSceneFile({ assets: "embed", host: captureHost(client, store) });
  const filename = `${fileBase(modelName)} scene.jscene`;
  downloadBlob(blob, filename);
  return { filename, bytes: blob.size };
}

/** Ring + scene: the ring as a compressed 3D file, with the whole scene inside. */
export async function exportRingWithScene(client: ViewerClient, store: EditorStore, modelName?: string | null): Promise<SavedFile> {
  const blob = await client.ring.exportGLB({ compress: true, embedScene: true, sceneHost: captureHost(client, store) });
  const filename = `${fileBase(modelName)} with scene.glb`;
  downloadBlob(blob, filename);
  return { filename, bytes: blob.size };
}

/**
 * Open scene file — replaces the look exactly, as ONE Undo step that covers
 * the viewer's half and the studio's half alike. Leaves Preview as customer
 * first: the scene must land on the design, not on a shopper's choices.
 *
 * If the import fails part-way, the snapshot from just before is opened again,
 * so a broken file cannot leave half a scene behind with nothing to undo.
 */
export async function openSceneFile(client: ViewerClient, store: EditorStore, file: File): Promise<SceneImportResult> {
  await checkSceneFile(file);
  if ((store.getState("ui") as UiState | undefined)?.customerPreview) await leaveCustomerPreview();
  const before = await client.ring.exportSceneJSON({ assets: "session", host: captureHost(client, store) });
  let result: SceneImportResult;
  try {
    result = await importOwn(client, store, file);
  } catch (e) {
    await importOwn(client, store, before).catch(() => undefined);
    throw e;
  }
  const after = await client.ring.exportSceneJSON({ assets: "session", host: captureHost(client, store) });
  basicEngineFor(store).recordExternal({
    label: "Open scene file",
    undo: async () => {
      await importOwn(client, store, before);
    },
    redo: async () => {
      await importOwn(client, store, after);
    },
  });
  return result;
}

/**
 * Open a scene on an EMPTY studio (the drop zone): the same import, with no
 * Undo step — there is no piece yet, and the history starts over when one
 * loads anyway.
 */
export async function restoreSceneFile(client: ViewerClient, store: EditorStore, file: File): Promise<SceneImportResult> {
  await checkSceneFile(file);
  return importOwn(client, store, file);
}

// ── what to tell the user ───────────────────────────────────────────────────

export interface SceneMessage {
  tone: "success" | "warning";
  title: string;
  description?: string;
}

const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

/** "Scene opened — 6 of 6 parts matched", and anything that did not come back. */
export function describeImport(r: SceneImportResult, title = "Scene opened"): SceneMessage {
  const { exact, byName, byKind, total } = r.matched;
  const matched = exact + byName + byKind;
  const notes: string[] = [];
  const head = total > 0 ? `${title} — ${matched} of ${plural(total, "part")} matched` : title;
  if (byKind > 0) notes.push(`${plural(byKind, "part")} matched by type, because the names differ from this ring's.`);
  if (r.unmatchedGroups.length > 0) {
    notes.push(`${plural(r.unmatchedGroups.length, "saved part")} found nothing to go on and ${r.unmatchedGroups.length === 1 ? "was" : "were"} left out.`);
  }
  const warnings = r.warnings.filter((w) => typeof w === "string" && w.trim());
  if (warnings.length) {
    notes.push(warnings.slice(0, 2).join(" "));
    if (warnings.length > 2) notes.push(`(${warnings.length - 2} more)`);
  }
  return {
    tone: r.unmatchedGroups.length > 0 || warnings.length > 0 ? "warning" : "success",
    title: head,
    ...(notes.length ? { description: notes.join(" ") } : {}),
  };
}

// ── one action at a time, shown everywhere ──────────────────────────────────

export type SceneBusy = "save" | "open" | "ring" | null;

interface BusyState {
  what: SceneBusy;
  subs: Set<() => void>;
}
const busy = new WeakMap<ViewerClient, BusyState>();

function busyOf(client: ViewerClient): BusyState {
  let b = busy.get(client);
  if (!b) {
    b = { what: null, subs: new Set() };
    busy.set(client, b);
  }
  return b;
}

/** What scene-file action is running on this viewer, if any. */
export function sceneBusy(client: ViewerClient): SceneBusy {
  return busyOf(client).what;
}

export function subscribeSceneBusy(client: ViewerClient, fn: () => void): () => void {
  const b = busyOf(client);
  b.subs.add(fn);
  return () => b.subs.delete(fn);
}

function setBusy(client: ViewerClient, what: SceneBusy) {
  const b = busyOf(client);
  b.what = what;
  for (const fn of b.subs) fn();
}

/** Run one scene-file action with the shared busy flag (every surface shows it). Rejects when one is already running. */
export async function withSceneBusy<T>(client: ViewerClient, what: Exclude<SceneBusy, null>, job: () => Promise<T>): Promise<T> {
  if (sceneBusy(client)) throw new Error("Another scene file is still being saved or opened.");
  setBusy(client, what);
  try {
    return await job();
  } finally {
    setBusy(client, null);
  }
}
